import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Rate, 
  Typography, 
  Avatar, 
  Space, 
  Tag, 
  Button, 
  Row, 
  Col, 
  Divider,
  Modal,
  Form,
  Input,
  message,
  Skeleton,
  Empty
} from 'antd';
import { 
  StarOutlined, 
  UserOutlined, 
  CalendarOutlined,
  LikeOutlined,
  DislikeOutlined,
  MessageOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { reviewService } from '../../../services/reviewService';
import ReviewCollection from './ReviewCollection';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const ReviewDisplay = ({ serviceId, showAll = false, limit = 5 }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    fetchReviews();
  }, [serviceId]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      let reviewsData;
      
      if (serviceId) {
        reviewsData = await reviewService.getServiceReviews(serviceId);
      } else {
        reviewsData = await reviewService.getRecentReviews(limit);
      }
      
      setReviews(reviewsData);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      message.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleWriteReview = (serviceData) => {
    setSelectedService(serviceData);
    setReviewModalVisible(true);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const renderReviewCard = (review) => (
    <Card
      key={review.id}
      style={{ marginBottom: '1rem' }}
      bodyStyle={{ padding: '1rem' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Avatar 
            size={40} 
            style={{ backgroundColor: '#1890ff' }}
          >
            {getInitials(review.customerName || 'Anonymous')}
          </Avatar>
          <div>
            <Text strong>{review.customerName || 'Anonymous Customer'}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '0.8rem' }}>
              {formatDate(review.createdAt)}
            </Text>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Rate 
            disabled 
            value={review.rating} 
            style={{ fontSize: '1rem' }}
          />
          <br />
          <Text type="secondary" style={{ fontSize: '0.8rem' }}>
            {review.serviceType}
          </Text>
        </div>
      </div>

      {review.title && (
        <Title level={5} style={{ margin: '0.5rem 0', fontSize: '1rem' }}>
          {review.title}
        </Title>
      )}

      <Paragraph style={{ marginBottom: '0.5rem' }}>
        {review.review}
      </Paragraph>

      {review.photos && review.photos.length > 0 && (
        <div style={{ marginBottom: '0.5rem' }}>
          <Text type="secondary" style={{ fontSize: '0.8rem' }}>
            Photos: {review.photos.length}
          </Text>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Tag color="green" icon={<CheckCircleOutlined />}>
            Verified Customer
          </Tag>
          {review.recommend === 'yes' && (
            <Tag color="blue">Recommends</Tag>
          )}
        </Space>
        
        <Space>
          <Button 
            type="text" 
            size="small" 
            icon={<LikeOutlined />}
            style={{ color: '#52c41a' }}
          >
            Helpful ({review.helpfulVotes || 0})
          </Button>
        </Space>
      </div>
    </Card>
  );

  const renderReviewStats = () => {
    if (reviews.length === 0) return null;

    const averageRating = reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length;
    const ratingDistribution = {
      5: reviews.filter(r => r.rating === 5).length,
      4: reviews.filter(r => r.rating === 4).length,
      3: reviews.filter(r => r.rating === 3).length,
      2: reviews.filter(r => r.rating === 2).length,
      1: reviews.filter(r => r.rating === 1).length
    };

    return (
      <Card style={{ marginBottom: '1.5rem' }}>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                {averageRating.toFixed(1)}
              </Title>
              <Rate disabled value={averageRating} style={{ fontSize: '1.2rem' }} />
              <br />
              <Text type="secondary">
                Based on {reviews.length} review{reviews.length !== 1 ? 's' : ''}
              </Text>
            </div>
          </Col>
          
          <Col xs={24} sm={16}>
            <div style={{ padding: '0.5rem 0' }}>
              {[5, 4, 3, 2, 1].map(rating => (
                <div key={rating} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <Text style={{ width: '20px' }}>{rating}</Text>
                  <StarOutlined style={{ color: '#faad14', margin: '0 0.5rem' }} />
                  <div 
                    style={{ 
                      flex: 1, 
                      height: '8px', 
                      backgroundColor: '#f0f0f0', 
                      borderRadius: '4px',
                      margin: '0 0.5rem'
                    }}
                  >
                    <div 
                      style={{ 
                        height: '100%', 
                        backgroundColor: '#faad14', 
                        borderRadius: '4px',
                        width: `${(ratingDistribution[rating] / reviews.length) * 100}%`
                      }}
                    />
                  </div>
                  <Text style={{ width: '30px', textAlign: 'right' }}>
                    {ratingDistribution[rating]}
                  </Text>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </Card>
    );
  };

  if (loading) {
    return (
      <div>
        <Skeleton active paragraph={{ rows: 3 }} />
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No reviews yet"
        >
          <Button 
            type="primary" 
            onClick={() => handleWriteReview({ serviceType: 'General Service' })}
          >
            Be the first to review
          </Button>
        </Empty>
      </Card>
    );
  }

  return (
    <div>
      {renderReviewStats()}
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <Title level={4} style={{ margin: 0 }}>
          Customer Reviews
        </Title>
        <Button 
          type="primary" 
          icon={<MessageOutlined />}
          onClick={() => handleWriteReview({ serviceType: 'General Service' })}
        >
          Write a Review
        </Button>
      </div>

      {reviews.map(renderReviewCard)}

      {!showAll && reviews.length >= limit && (
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <Button type="link">
            View All Reviews ({reviews.length})
          </Button>
        </div>
      )}

      <ReviewCollection
        visible={reviewModalVisible}
        onClose={() => setReviewModalVisible(false)}
        serviceDetails={selectedService}
      />
    </div>
  );
};

export default ReviewDisplay;
